package org.example.project.enginevertrieb

import androidx.compose.runtime.*
import kotlinx.browser.window
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.await
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import kotlinx.coroutines.async
import kotlinx.coroutines.coroutineScope
import kotlinx.coroutines.sync.Mutex
import kotlinx.coroutines.sync.withLock
import org.example.project.cleaning.SharedWorkspaceStyle
import org.example.project.dashboard.AppDialogs
import org.example.project.enginechat.ChatMessageList
import org.example.project.enginetabular.TabularSplitScreen
import org.example.project.enginetabular.TabularThreeSideSplitScreen
import org.example.project.service.KontaktKalenderPopupService
import org.example.project.service.LoginService
import org.example.project.service.RollenService
import org.example.project.settings.*
import org.example.project.utils.All
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

/** Independent LinkedIn workspace. Reading or choosing a contact does not adopt a lead. */
@Composable
fun LinkedInLeadUebernahme(node: String, initialContactId: String? = null, initialCheckpoint: String = "MY_LEADS") {
    if (node !in setOf("SYS", "CUS")) return
    val scope = rememberCoroutineScope()
    val sharedKpis = LocalLinkedInKpis.current
    var contacts by remember { mutableStateOf(sharedKpis?.contacts.orEmpty()) }
    var contactId by remember { mutableStateOf(initialContactId) }
    var initialContactEffect by remember { mutableStateOf(true) }
    var lead by remember { mutableStateOf<All?>(null) }
    var displayedContactId by remember { mutableStateOf<String?>(null) }
    var pendingContactId by remember { mutableStateOf<String?>(null) }
    var messages by remember { mutableStateOf<List<All>>(emptyList()) }
    var types by remember { mutableStateOf<List<VertriebsindexApi.LeadTypEintrag>>(emptyList()) }
    var calls by remember { mutableStateOf<List<All>>(emptyList()) }
    var dmEntries by remember { mutableStateOf<List<All>>(emptyList()) }
    var scripts by remember { mutableStateOf<List<All>>(emptyList()) }
    var appointmentScripts by remember { mutableStateOf<List<All>>(emptyList()) }
    var profileEditing by remember { mutableStateOf(window.location.hash.contains("salesSettings=profiles")) }
    var dispatchAction by remember(contactId) { mutableStateOf<String?>(null) }
    var materialChannelOpen by remember(contactId) { mutableStateOf(false) }
    var materialSelectedChannel by remember(contactId) { mutableStateOf<String?>(null) }
    var dispatchText by remember(contactId) { mutableStateOf("") }
    var dispatchReady by remember(contactId) { mutableStateOf(false) }
    var dispatchCopying by remember(contactId) { mutableStateOf(false) }
    var dispatchError by remember(contactId) { mutableStateOf("") }
    var checkpointDeadlines by remember { mutableStateOf<Map<String, Int>>(emptyMap()) }
    var view by remember { mutableStateOf(initialCheckpoint.takeIf { value -> isCheckpointView(value) } ?: "MY_LEADS") }
    var zusatzHeaders by remember { mutableStateOf<List<VertriebsindexApi.ZusatzHeader>>(emptyList()) }
    var loading by remember { mutableStateOf(sharedKpis?.loaded != true) }
    var bookingPending by remember { mutableStateOf(false) }
    val writeQueues = remember { sharedKpis?.writeQueues ?: mutableMapOf<String, Mutex>() }
    val writeRevisions = remember { sharedKpis?.writeRevisions ?: mutableMapOf<String, Int>() }
    val messageCache = remember(LinkedInSyncState.version) { mutableMapOf<String, List<All>>() }
    val messageCacheTimes = remember(LinkedInSyncState.version) { mutableMapOf<String, Double>() }
    var finishOpen by remember { mutableStateOf(false) }
    var calendarPopupId by remember { mutableStateOf<String?>(null) }
    val calendarOpen = KontaktKalenderPopupService.getPopups(node).any { it.id == calendarPopupId }
    val canWrite = RollenService.hasActionPermission("LinkedInFlow", RollenService.ACTION_EDIT)

    fun mergeStatus(value: All, status: All?): All = if (status == null) value else value.copy(
        linkedinAusgeblendet = value.linkedinAusgeblendet || status.linkedinAusgeblendet,
        linkedinHatNachrichten = status.linkedinHatNachrichten,
        linkedinNetzwerkstatus = status.linkedinNetzwerkstatus, linkedinSnapshotGeloescht = status.linkedinSnapshotGeloescht,
        linkedinLetzteAntwortAm = status.linkedinLetzteAntwortAm,
        linkedinFollowUpAm = status.linkedinFollowUpAm, linkedinFollowUpFaellig = status.linkedinFollowUpFaellig,
        linkedinAngeschrieben = value.linkedinAngeschrieben || status.linkedinAngeschrieben,
        linkedinDmGesendetAm = value.linkedinDmGesendetAm.ifBlank { status.linkedinDmGesendetAm },
        linkedinLetzteNachrichtAm = status.linkedinLetzteNachrichtAm.ifBlank { value.linkedinLetzteNachrichtAm },
        linkedinAntwortErhalten = status.linkedinAntwortErhalten, linkedinGeschlossen = status.linkedinGeschlossen,
        linkedinKontaktUnbearbeitet = status.linkedinKontaktUnbearbeitet, linkedinLeadOffen = status.linkedinLeadOffen,
        linkedinLeadAktiv = status.linkedinLeadAktiv, linkedinMaterialOffen = status.linkedinMaterialOffen,
        linkedinTerminGebucht = status.linkedinTerminGebucht,
        vertriebCheckpoint = status.vertriebCheckpoint, vertriebCheckpointAm = status.vertriebCheckpointAm,
        vertriebCheckpointVerlauf = status.vertriebCheckpointVerlauf, vertriebMeilensteine = status.vertriebMeilensteine,
        vertriebLetzteAktionAm = status.vertriebLetzteAktionAm, vertriebWiedervorlageAm = status.vertriebWiedervorlageAm,
        vertriebFollowUpAm = status.vertriebFollowUpAm, vertriebFollowUpFaellig = status.vertriebFollowUpFaellig,
        vertriebAbschlussErgebnis = status.vertriebAbschlussErgebnis, vertriebAbschlussGrund = status.vertriebAbschlussGrund
    )
    suspend fun loadContacts() {
        if (sharedKpis != null) {
            sharedKpis.refresh(initialOnly = true)
            contacts = sharedKpis.contacts
            checkpointDeadlines = sharedKpis.deadlines
        } else {
            val data = LinkedInFlowApi.chat()
            contacts = data.linkedinKontakte.filterNot { it.linkedinSnapshotGeloescht }
            checkpointDeadlines = data.vertriebCheckpointFristen
        }
    }
    suspend fun refreshStatuses() {
        if (sharedKpis != null) sharedKpis.refresh() else loadContacts()
    }
    fun accept(saved: All, externalId: String, preserveFollowUp: Boolean = true) {
        val previous = contacts.firstOrNull { it.id == externalId }
        // Notes/table responses contain stored fields, not calculated FU values.
        val accepted = if (preserveFollowUp && saved.vertriebFollowUpAm.isBlank() && saved.vertriebCheckpoint !in setOf("CLOSED", "REPLIED", "PARKED", "IN_REVIEW") &&
            previous?.vertriebCheckpoint == saved.vertriebCheckpoint) saved.copy(
                vertriebFollowUpAm = previous.vertriebFollowUpAm,
                vertriebFollowUpFaellig = previous.vertriebFollowUpFaellig
            ) else saved
        val row = accepted.copy(id = externalId, linkedinProspectId =
            if (saved.id == externalId) previous?.linkedinProspectId.orEmpty() else saved.id)
        if (contactId == externalId) {
            lead = row
            displayedContactId = externalId
        }
        contacts = contacts.map { old -> if (old.id == externalId) row else old }
        sharedKpis?.replace(row)
    }
    fun enqueueWrite(id: String, optimistic: All?, preserveFollowUp: Boolean = true, write: suspend () -> All) {
        val previous = contacts.firstOrNull { it.id == id }
        val version = (writeRevisions[id] ?: 0) + 1
        writeRevisions[id] = version
        val queue = writeQueues.getOrPut(id) { Mutex() }
        sharedKpis?.beginWrite()
        if (optimistic != null) accept(optimistic, id, preserveFollowUp = false)
        (sharedKpis?.writeScope ?: scope).launch {
            try {
                val saved = queue.withLock { write() }
                if (writeRevisions[id] == version) accept(saved, id, preserveFollowUp)
            } catch (e: CancellationException) { throw e }
            catch (e: Exception) {
                if (writeRevisions[id] == version && previous != null) accept(previous, id)
                AppDialogs.alert("Speichern fehlgeschlagen (" +
                    listOfNotNull(previous?.vorname, previous?.nachname, previous?.firma).filter { it.isNotBlank() }.joinToString(" / ") +
                    "): " + (e.message ?: "Bitte erneut versuchen."))
            } finally { sharedKpis?.finishWrite() }
        }
    }
    fun save(value: All, action: String = "save", after: () -> Unit = {}) {
        val id = contactId ?: return
        if (!canWrite || loading || displayedContactId != id) return
        enqueueWrite(id, value) { LinkedInFlowApi.save(id, value, action) }
        after()
    }
    fun refresh() {
        scope.launch {
            try { refreshStatuses() }
            catch (e: CancellationException) { throw e }
            catch (e: Exception) { AppDialogs.alert(e.message ?: "LinkedIn-Abgleich fehlgeschlagen.") }
        }
    }
    fun perform(action: String, confirmed: Boolean = false, bookingId: String = "", note: String = "", result: String = "", reminder: String = "") {
        val id = contactId ?: return
        val current = lead ?: return
        if (!canWrite || loading || displayedContactId != id) return
        val optimistic = optimisticLinkedInCheckpoint(current, action, result, note)
        enqueueWrite(id, optimistic, preserveFollowUp = false) { LinkedInFlowApi.checkpoint(id, action, confirmed, bookingId, note, result, reminder) }
        finishOpen = false; materialChannelOpen = false; materialSelectedChannel = null; dispatchAction = null; dispatchText = ""; dispatchReady = false; dispatchError = ""
        if (action == "IN_CALL") {
            view = "IN_CALL"
            sharedKpis?.selected = "IN_CALL"
        } else if (!checkpointMatches(optimistic, view)) {
            contactId = null
            displayedContactId = null
            lead = null
            messages = emptyList()
        }
    }
    fun bookAppointment(kind: String) {
        val current = lead ?: return
        val id = contactId ?: return
        if (!canWrite || bookingPending || loading || calendarOpen || pendingContactId != null || displayedContactId != id) return
        bookingPending = true
        scope.launch {
            try {
                val saved = LinkedInFlowApi.save(id, current)
                accept(saved, id)
                calendarPopupId = KontaktKalenderPopupService.openPopup(
                    node, "SYS", LoginService.getId(), LoginService.getFirmenId(),
                    (if (kind == "APPOINTMENT_BOOKED") "Termin buchen" else "Call buchen") + " · " + saved.firma,
                    bookingTemplate = All(akquiseProspectId = saved.id, leadQuelle = saved.leadQuelle, akquiseTyp = "DM", firma = saved.firma,
                        ansprechpartner = saved.ansprechpartner, email = saved.email, telefon = saved.telefon,
                        terminArt = if (kind == "APPOINTMENT_BOOKED") "Termin" else "Call", bezeichnung = saved.firma),
                    onBookingSaved = { booking ->
                        val action = if (booking.terminArt == "Call") "CALL_BOOKED" else "APPOINTMENT_BOOKED"
                        enqueueWrite(id, optimisticLinkedInCheckpoint(saved, action, "", ""), preserveFollowUp = false) {
                            LinkedInFlowApi.checkpoint(id, action, bookingId = booking.id)
                        }
                    })
            } catch (e: CancellationException) { throw e }
            catch (e: Exception) { AppDialogs.alert(e.message ?: "Kalender konnte nicht geöffnet werden.") }
            finally { bookingPending = false }
        }
    }
    fun beginDispatch(action: String) {
        val current = lead ?: return
        val id = contactId ?: return
        if (action == "FOLLOWUP" && !current.vertriebFollowUpFaellig) return
        if (!canWrite || loading || calendarOpen || profileEditing || dispatchAction != null ||
            pendingContactId != null || displayedContactId != id || current.vertriebCheckpoint == "CLOSED") return
        dispatchError = ""
        dispatchReady = false
        if (action == "MATERIAL_SENT") {
            dispatchAction = action
            dispatchText = linkedInDispatchText(dmEntries, types, current, LoginService.getUser()?.leadTyp.orEmpty(), action)
            materialSelectedChannel = null
            materialChannelOpen = true
            return
        }
        val text = linkedInDispatchText(dmEntries, types, current, LoginService.getUser()?.leadTyp.orEmpty(), action)
        try {
            val copied = copyLinkedInTextAndOpenProfile(text, current.linkedin)
            dispatchAction = action
            dispatchText = text
            materialSelectedChannel = "linkedin"
            materialChannelOpen = true
            dispatchCopying = true
            scope.launch {
                try {
                    copied.await()
                    if (contactId == id && dispatchAction == action) dispatchReady = true
                } catch (e: CancellationException) { throw e }
                catch (e: Exception) {
                    if (contactId == id) {
                        dispatchError = e.message ?: "Nachricht konnte nicht kopiert werden. Bitte die Zwischenablage im Browser erlauben."
                    }
                } finally { if (contactId == id) dispatchCopying = false }
            }
        } catch (e: Exception) { dispatchError = e.message ?: "LinkedIn-Profil konnte nicht geöffnet werden." }
    }
    fun nextAction(action: String) {
        when (action) {
            "DM_SENT", "FOLLOWUP", "MATERIAL_SENT" -> beginDispatch(action)
            "CALL_BOOKED", "APPOINTMENT_BOOKED" -> bookAppointment(action)
            "CALL_COMPLETED", "CALL_DONE", "APPOINTMENT_DONE" -> {
                dispatchAction = action; dispatchText = ""; dispatchError = ""
                dispatchReady = true; materialSelectedChannel = null; materialChannelOpen = true
            }
            "REPLIED" -> AppDialogs.confirm(
                "Ist die erste Rückantwort tatsächlich eingegangen?",
                onConfirm = { perform(action, confirmed = true) })
            "CLOSED" -> finishOpen = true
            else -> perform(action)
        }
    }

    LaunchedEffect(Unit) {
        loading = true
        try {
            coroutineScope {
                val headers = async { VertriebsindexApi.loadHeaders() }
                val leadTypes = async { VertriebsindexApi.loadLeadtypen() }
                val profiles = async { FlowProfileApi.load() }
                val dm = async { LinkedInDmApi.index() }
                val callScripts = async { VertriebsindexApi.loadScriptIndex() }
                val terminScripts = async { VertriebsindexApi.loadScriptIndex("TERMIN") }
                val contactLoad = async { loadContacts() }
                zusatzHeaders = headers.await(); types = leadTypes.await(); calls = profiles.await()
                dmEntries = dm.await(); scripts = callScripts.await(); appointmentScripts = terminScripts.await()
                contactLoad.await()
            }
        } catch (e: CancellationException) { throw e }
        catch (e: Exception) { AppDialogs.alert(e.message ?: "DM Flow konnte nicht geladen werden.") }
        finally { loading = false }
    }
    LaunchedEffect(sharedKpis?.contacts, loading) {
        if (sharedKpis != null && sharedKpis.loaded) {
            contacts = sharedKpis.contacts
            checkpointDeadlines = sharedKpis.deadlines
            val status = contacts.firstOrNull { it.id == contactId }
            if (displayedContactId == contactId) lead = lead?.let { mergeStatus(it, status) }
        }
    }
    LaunchedEffect(contactId, loading, LinkedInSyncState.version) {
        if (loading) return@LaunchedEffect
        val first = initialContactEffect
        initialContactEffect = false
        val id = contactId
        if (id == null) {
            lead = null; displayedContactId = null; messages = emptyList(); pendingContactId = null
            return@LaunchedEffect
        }
        val cached = contacts.firstOrNull { it.id == id }
        lead = cached
        displayedContactId = if (cached != null) id else null
        messages = messageCache[id].orEmpty()
        try {
            if (cached == null) {
                pendingContactId = id
                val loaded = LinkedInFlowApi.lead(id)
                if (contactId == id) { lead = loaded; displayedContactId = id }
            }
            if (first && initialContactId != null) view = lead?.let(::checkpointView) ?: "MY_LEADS"
            if (contactId == id) pendingContactId = null
            if (id !in messageCache || kotlin.js.Date.now() - (messageCacheTimes[id] ?: 0.0) >= 180_000) {
                val transcript = LinkedInFlowApi.messages(id)
                messageCache[id] = transcript
                messageCacheTimes[id] = kotlin.js.Date.now()
                if (contactId == id) messages = transcript
            }
        } catch (e: CancellationException) { throw e }
        catch (e: Exception) { AppDialogs.alert(e.message ?: "LinkedIn-Kontakt konnte nicht geladen werden.") }
        finally { if (pendingContactId == id) pendingContactId = null }
    }
    // The dashboard owns the single background contact refresh. Standalone flows
    // keep one poll of their own; neither path blocks the editor or selection.
    LaunchedEffect(sharedKpis, LinkedInSyncState.version) {
        if (sharedKpis != null) return@LaunchedEffect
        while (true) {
            delay(180_000)
            try { refreshStatuses() }
            catch (e: CancellationException) { throw e }
            catch (_: Exception) { /* Keep the working copy available. */ }
        }
    }

    fun belongs(item: All, target: String): Boolean = checkpointMatches(item, target)
    val visible = contacts.filter { belongs(it, view) }.sortedWith { a, b ->
        if (a.vertriebFollowUpFaellig != b.vertriebFollowUpFaellig) return@sortedWith if (a.vertriebFollowUpFaellig) -1 else 1
        val last = a.nachname.asDynamic().localeCompare(b.nachname, "de", js("({sensitivity:'base'})")) as Int
        if (last != 0) last else {
            val first = a.vorname.asDynamic().localeCompare(b.vorname, "de", js("({sensitivity:'base'})")) as Int
            if (first != 0) first else a.firma.asDynamic().localeCompare(b.firma, "de", js("({sensitivity:'base'})")) as Int
        }
    }
    val enabled = canWrite && lead != null && lead?.vertriebCheckpoint != "CLOSED" && lead?.linkedinAusgeblendet != true && lead?.linkedinSnapshotGeloescht != true && displayedContactId == contactId && pendingContactId == null && !loading && !calendarOpen && !finishOpen && !profileEditing && dispatchAction == null
    val method = if (lead?.vertriebCheckpoint in setOf("IN_CALL")) "CALL" else "DM"
    val chosenCallId = flowIdForLead(lead, types, method)
    val currentCall = calls.firstOrNull { it.id == chosenCallId }
    var conversationStage by remember(contactId, chosenCallId) { mutableStateOf(1) }
    var conversationUsed by remember(contactId, chosenCallId) { mutableStateOf<Set<String>>(emptySet()) }
    var conversationAnswers by remember(contactId, chosenCallId) { mutableStateOf<Map<String, Set<Int>>>(emptyMap()) }
    val conversation = resolveScriptDefinitions(currentCall?.vertriebCallFlow.orEmpty(), if (currentCall?.vertriebScriptBereich == "TERMIN") appointmentScripts else scripts)
        .filter { it.akquiseDefinitionAktiv && it.akquiseDefinitionTyp.isNotBlank() && it.akquiseDefinitionText.isNotBlank() }
        .groupBy { it.akquiseDefinitionTyp }.entries.sortedBy { it.value.minOf { row -> row.akquiseDefinitionTypPrio } }
    val previewAction = dispatchAction
        ?: when (lead?.vertriebCheckpoint) { "MY_LEADS" -> "DM_SENT"; "MATERIAL_REQUESTED" -> "MATERIAL_SENT"; else -> "FOLLOWUP" }
    val previewText = if (dispatchAction != null) dispatchText
        else lead?.let { linkedInDispatchText(dmEntries, types, it, LoginService.getUser()?.leadTyp.orEmpty(), previewAction) }.orEmpty()
    val canSendMessage = previewText.isNotBlank() && lead?.let { linkedInDispatchProfileUrl(it.linkedin).isNotBlank() } == true
    LaunchedEffect(sharedKpis?.selectionVersion) {
        sharedKpis?.selected?.takeIf { target -> isCheckpointView(target) }?.let { target ->
            view = target
            if (contacts.firstOrNull { it.id == contactId }?.let { !belongs(it, target) } == true) contactId = null
        }
    }
    LaunchedEffect(view) { sharedKpis?.selected = view }
    SideEffect {
        sharedKpis?.enabled = !profileEditing && !loading && !calendarOpen && !finishOpen &&
            dispatchAction == null && pendingContactId == null
    }
    DisposableEffect(sharedKpis) { onDispose { sharedKpis?.enabled = true } }
    if (profileEditing) FlowProfileSettings(node, chosenCallId, onClose = {
        profileEditing = false
        scope.launch {
            try { types = VertriebsindexApi.loadLeadtypen(); calls = FlowProfileApi.load(); scripts = VertriebsindexApi.loadScriptIndex(); appointmentScripts = VertriebsindexApi.loadScriptIndex("TERMIN"); refreshStatuses() }
            catch (e: CancellationException) { throw e }
            catch (e: Exception) { AppDialogs.alert(e.message ?: "Profile konnten nicht aktualisiert werden.") }
        }
    }) else Div({ style { height(100.percent); minHeight(0.px); overflow("hidden"); display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); color(UISettings.STATE_BUTTON_TEXT) } }) {
        Style(SharedWorkspaceStyle)
        if (sharedKpis == null && !profileEditing) LinkedInCheckpointTiles(contacts, view,
            !loading && !calendarOpen && !finishOpen && dispatchAction == null && pendingContactId == null,
            checkpointDeadlines, onSelect = { target ->
                view = target
                if (contacts.firstOrNull { it.id == contactId }?.let { !belongs(it, target) } == true) contactId = null
            })
        if (sharedKpis == null && !profileEditing) Div({ style { width(100.percent); height(1.px); backgroundColor(Color("rgba(255,255,255,.14)")); flexShrink(0) } })
            Div({ style { flex("1"); minHeight(0.px) } }) {
                TabularThreeSideSplitScreen(storageId = "linkedin-lead-uebernahme-$node",
                    dividerLineColor = "rgba(255,255,255,0.32)",
                    screen1 = {
                        Div({ style { display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); height(100.percent); minHeight(0.px); padding(12.px); boxSizing("border-box") } }) {
                            Div({ style { display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(8.px); width(100.percent); minWidth(0.px); flexShrink(0) } }) {
                                Div({ style { width(36.px); flexShrink(0) } }) {
                                Button(attrs = {
                                    attr("aria-label", "Einstellungen"); attr("title", "Einstellungen")
                                    if (!canWrite || loading || calendarOpen || finishOpen || dispatchAction != null) attr("disabled", "")
                                    style { width(36.px); height(36.px); padding(0.px); border(0.px); backgroundColor(Color.transparent); cursor("pointer") }
                                    onClick {
                                        profileEditing = true
                                    }
                                }) {
                                    Img(src = "images/einstellungen.svg", attrs = { attr("alt", ""); style { width(21.px); height(21.px); property("filter", "brightness(0) invert(1)") } })
                                }
                                }
                                Div({ style { flex("1"); minWidth(0.px) } }) {
                            AppDropdown(
                                options = visible.map { All(key = it.id, leadQuelle = it.leadQuelle.ifBlank { "LinkedIn" }, label = listOf(
                                    listOf(it.vorname, it.nachname).filter { value -> value.isNotBlank() }.joinToString(" ").ifBlank { it.ansprechpartner },
                                    it.firma).filter { value -> value.isNotBlank() }.joinToString(" · ")) },
                                optionIcon = { if (it.leadQuelle == "LinkedIn") "linkedin" else "webseite" },
                                selectedValue = contactId,
                                onChange = { contactId = it }, placeholder = "Kontakt auswählen",
                                enabled = !loading && !profileEditing && !calendarOpen && !finishOpen && dispatchAction == null && pendingContactId == null,
                                appearance = AppDropdownAppearance.Field, menuMode = AppDropdownMenuMode.Overlay,
                                attrs = { attr("aria-label", "Kontakt auswählen"); style { width(100.percent); minWidth(0.px); height(36.px) } },
                                optionTextColor = { option -> if (visible.firstOrNull { it.id == option.key }?.vertriebFollowUpFaellig == true) "#ff6262" else null })
                                }
                            }
                            Div({ style { flex("1"); minHeight(0.px); overflowY("auto") } }) {
                                key(contactId) { FlowContactDetails(lead ?: All(), zusatzHeaders, enabled, isLinkedIn = true,
                                    onSave = { value, _ -> save(value) }) }
                                lead?.let { current ->
                                    LinkedInLeadActions(current, enabled, ::nextAction, canSendMessage = canSendMessage,
                                        canStartCall = true)
                                    if (dispatchError.isNotBlank() && !materialChannelOpen) Div({ attr("role", "alert"); style { color(UISettings.ACCENT); fontSize(12.px) } }) { Text(dispatchError) }
                                }
                            }
                        }
                    },
                    screen2 = {
                        if (lead?.vertriebCheckpoint in setOf("IN_CALL")) Div({ style { height(100.percent); overflowY("auto"); padding(18.px); boxSizing("border-box") } }) {
                            if (conversation.isEmpty()) Div({ style { textAlign("center"); padding(24.px) } }) { Text("Im Profil ist noch kein Gesprächsflow hinterlegt.") }
                            SalesConversationSteps(conversation, conversationStage, enabled, conversationUsed, conversationAnswers,
                                onUse = { conversationUsed = conversationUsed + it }, onAnswersChange = { conversationAnswers = it },
                                onStageChange = { conversationStage = it }, log = { kind, text ->
                                    val id = contactId
                                    if (id != null && enabled) {
                                        val eventId = js("crypto.randomUUID()") as String
                                        enqueueWrite(id, null) { LinkedInFlowApi.recordConversation(id, kind, text, eventId) }
                                    }
                                })
                        } else Div({ style { height(100.percent); minHeight(0.px); overflowY("auto"); display(DisplayStyle.Flex); flexDirection(FlexDirection.Column) } }) {
                            if (!profileEditing && lead?.vertriebCheckpoint == "MY_LEADS") {
                                LinkedInDispatchPreview(previewAction, previewText)
                            }
                            Div({ style { flex("1"); minHeight(180.px); overflow("hidden") } }) {
                                ChatMessageList(messages, { it.linkedinNachrichtEigen }, { "" }, scrollable = true)
                            }

                        }
                    },
                    screen3 = {
                        Div({ style { height(100.percent); minHeight(0.px); padding(12.px); boxSizing("border-box") } }) {
                            TabularSplitScreen(initialRatio = .42f, minRatio = .18f, maxRatio = .75f,
                                screen1 = { Div({ style { height(100.percent); minHeight(0.px); display(DisplayStyle.Flex); flexDirection(FlexDirection.Column) } }) {
                                    Div({ style { height(100.percent); overflowY("auto") } }) { LinkedInQualification(lead ?: All(),
                                        currentCall?.vertriebCallCheckboxen.orEmpty().filter { it.linkedinDmCheckboxAktiv && it.linkedinDmCheckboxText.isNotBlank() },
                                        enabled, update = { change -> lead = lead?.let(change) }, showEmptyState = false,
                                        save = { lead?.let { save(it) } }) }
                                } },
                                screen2 = { lead?.let { current -> LeadInfoNotes(current, enabled, popupErrors = true,
                                    addInfo = { value, text ->
                                        val id = contactId ?: error("LinkedIn-Kontakt fehlt.")
                                        val optimistic = value.copy(leadInfos = value.leadInfos + All(leadInfoText = text))
                                        enqueueWrite(id, optimistic) {
                                            val adopted = LinkedInFlowApi.save(id, value)
                                            VertriebsindexApi.addInfo(adopted.id, text) ?: adopted
                                        }
                                        null
                                    }, onUpdated = { updated -> contactId?.let { accept(updated, it) } }) } }
                            )
                        }
                    }
                )
            }
        if (materialChannelOpen) lead?.let { current ->
            LinkedInDispatchPopup(current, dispatchText, dispatchError, dispatchCopying,
                action = dispatchAction.orEmpty(),
                selectedChannel = materialSelectedChannel, ready = dispatchReady,
                onConfirm = { if (dispatchReady && !dispatchCopying) dispatchAction?.let { perform(it, confirmed = true, note = dispatchText) } },
                onClose = { materialChannelOpen = false; dispatchAction = null; dispatchText = ""; dispatchError = "" },
                onSelect = { channel ->
                    val id = contactId
                    materialSelectedChannel = channel; dispatchReady = false
                    try {
                        val sent = sendMaterialText(dispatchText, current, channel)
                        dispatchCopying = true; dispatchError = ""
                        scope.launch {
                            try {
                                sent.await()
                                if (contactId == id && dispatchAction == "MATERIAL_SENT") {
                                    dispatchReady = true
                                }
                            } catch (e: CancellationException) { throw e }
                            catch (e: Exception) { if (contactId == id) dispatchError = e.message ?: "Versandkanal konnte nicht geöffnet werden." }
                            finally { if (contactId == id) dispatchCopying = false }
                        }
                    } catch (e: Exception) { dispatchError = e.message ?: "Versandkanal konnte nicht geöffnet werden." }
                })
        }
        if (finishOpen) lead?.let { current -> LinkedInCheckpointFinish(current, true,
            onClose = { finishOpen = false }, onFinish = { result, note -> perform("CLOSED", note = note, result = result) }) }
    }
}
